import { useEffect, useRef, useState } from 'react'
import { CCQ_CARD_ORDER, sumCounts, type CcqCard, type CcqCounts } from './ccqResultView'

export interface CcqWave {
  checked: number
  counts: CcqCounts
}

// Counts aren't tweened independently: the delta is replayed as interleaved single arrivals, so on every frame
// they sum to the counter, stay monotone and never pass their targets.

// Well inside RESULTS_POLL_INTERVAL_MS (4s), so a tween finishes before the next wave.
export const CCQ_WAVE_TWEEN_MS = 1500

// Bresenham-style interleave: at every step, the card furthest behind its proportional share goes next.
export const buildTweenSequence = (from: CcqCounts, to: CcqCounts): CcqCard[] => {
  const deltas = CCQ_CARD_ORDER.map((card) => Math.max(to[card] - from[card], 0))
  const total = deltas.reduce((sum, delta) => sum + delta, 0)
  const assigned = CCQ_CARD_ORDER.map(() => 0)

  return Array.from({ length: total }, (_, index) => {
    const step = index + 1
    let next = 0
    let bestDeficit = -Infinity
    deltas.forEach((delta, cardIndex) => {
      if (assigned[cardIndex] >= delta) {
        return
      }
      const deficit = (delta * step) / total - assigned[cardIndex]
      if (deficit > bestDeficit) {
        bestDeficit = deficit
        next = cardIndex
      }
    })
    assigned[next] += 1
    return CCQ_CARD_ORDER[next]
  })
}

export const countsAfterSteps = (from: CcqCounts, sequence: CcqCard[], steps: number): CcqCounts =>
  sequence.slice(0, steps).reduce<CcqCounts>((counts, card) => ({ ...counts, [card]: counts[card] + 1 }), { ...from })

export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3

export const isBackwards = (from: CcqWave, to: CcqWave): boolean => CCQ_CARD_ORDER.some((card) => to.counts[card] < from.counts[card])

export interface CcqWaveDisplay {
  checked: number
  counts: CcqCounts
  isAnimating: boolean
}

export const useWaveTween = (target: CcqWave, animate: boolean): CcqWaveDisplay => {
  const [display, setDisplay] = useState<CcqWaveDisplay>({ checked: target.checked, counts: target.counts, isAnimating: false })
  // Where the next tween starts from: whatever is on screen, even mid-tween.
  const shownRef = useRef<CcqCounts>(target.counts)

  useEffect(() => {
    const from = shownRef.current
    let frame = 0

    const show = (counts: CcqCounts, isAnimating: boolean) => {
      shownRef.current = counts
      setDisplay({ checked: sumCounts(counts), counts, isAnimating })
    }

    const sequence = animate && !isBackwards({ checked: sumCounts(from), counts: from }, target) ? buildTweenSequence(from, target.counts) : []
    // A poll can bring a new counts object with the same counts; there's nothing to count then.
    if (sequence.length === 0) {
      frame = requestAnimationFrame(() => {
        show(target.counts, false)
      })
      return () => {
        cancelAnimationFrame(frame)
      }
    }

    const startedAt = performance.now()
    let shownSteps = -1

    const step = (now: number) => {
      const t = Math.min((now - startedAt) / CCQ_WAVE_TWEEN_MS, 1)
      const steps = Math.round(easeOutCubic(t) * sequence.length)
      if (steps !== shownSteps || t >= 1) {
        shownSteps = steps
        show(countsAfterSteps(from, sequence, steps), t < 1)
      }
      if (t < 1) {
        frame = requestAnimationFrame(step)
      }
    }

    frame = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(frame)
    }
  }, [target, animate])

  return display
}
